import type { ChatMessage, Conversation, WorkspaceFile } from '@auw/workspace-utils';
import { collabWS } from '@auw/api-client';
import { clampMessages } from '../domain/conversationMemory';
import { mergeHydratedMessages } from '../domain/messageTimeline';
import { trackStaleLoadingGuard } from '@auw/workspace-utils';

interface SelectionEffectsState {
  activeId: string | null;
  conversations: Record<string, Conversation>;
  loadingConversationId: string | null;
  sessionFiles: WorkspaceFile[];
}

interface RunConversationSelectionEffectsArgs {
  previousConversation: Conversation | null;
  nextConversationId: string | null;
  nextConversation: Conversation | null;
  getState: () => SelectionEffectsState;
  setState: (
    patch:
      | Partial<SelectionEffectsState>
      | ((state: SelectionEffectsState) => Partial<SelectionEffectsState> | SelectionEffectsState),
  ) => void;
  fetchMessageHistory: (conversationId: string) => Promise<ChatMessage[]>;
  loadConversationFiles: (conversationId: string) => Promise<void>;
  isTeamDeletedSharedGroup: (conversation: Conversation | null | undefined) => boolean;
  enrichSharedGroupMembers: (conversationId: string, teamId: string) => void;
}

const LOADING_INDICATOR_DELAY_MS = 140;
let loadingIndicatorTimer: ReturnType<typeof setTimeout> | null = null;
let activeConversationRequestSeq = 0;

export function clearSelectionLoadingIndicator(): void {
  if (!loadingIndicatorTimer) return;
  clearTimeout(loadingIndicatorTimer);
  loadingIndicatorTimer = null;
}

export function ensureConversationRealtimeRoom(
  conversationId: string | null,
  conversation: Conversation | null,
  isTeamDeletedSharedGroup: (conversation: Conversation | null | undefined) => boolean,
): void {
  if (!conversationId || !conversation?.sharedGroup || isTeamDeletedSharedGroup(conversation)) return;
  void collabWS.connect();
  collabWS.joinRoom(conversationId);
}

export function runConversationSelectionEffects({
  previousConversation,
  nextConversationId,
  nextConversation,
  getState,
  setState,
  fetchMessageHistory,
  loadConversationFiles,
  isTeamDeletedSharedGroup,
  enrichSharedGroupMembers,
}: RunConversationSelectionEffectsArgs): void {
  clearSelectionLoadingIndicator();
  const activationSeq = ++activeConversationRequestSeq;

  if (previousConversation?.sharedGroup && previousConversation.id !== nextConversationId) {
    collabWS.leaveRoom(previousConversation.id);
  }

  ensureConversationRealtimeRoom(nextConversationId, nextConversation, isTeamDeletedSharedGroup);

  const teamId = nextConversation?.sharedGroup?.team_id;
  if (nextConversationId && teamId) {
    enrichSharedGroupMembers(nextConversationId, teamId);
  }

  const needsLoad = nextConversationId && nextConversation && !nextConversation.historyLoaded;
  if (needsLoad) {
    const requestId = nextConversationId;
    const isLatestActivation = () => {
      const stateNow = getState();
      return activeConversationRequestSeq === activationSeq && stateNow.activeId === requestId;
    };

    const settleLoadingIfLatest = () => {
      clearSelectionLoadingIndicator();
      if (isLatestActivation()) {
        setState({ loadingConversationId: null });
        return;
      }
      trackStaleLoadingGuard();
    };

    loadingIndicatorTimer = setTimeout(() => {
      loadingIndicatorTimer = null;
      if (!isLatestActivation()) return;
      const stateNow = getState();
      const latestConversation = stateNow.conversations[requestId];
      if (!latestConversation || latestConversation.historyLoaded || latestConversation.messages.length > 0) return;
      setState({ loadingConversationId: requestId });
    }, LOADING_INDICATOR_DELAY_MS);

    const applyMessages = (messages: ChatMessage[], fromCache: boolean) => {
      const state = getState();
      const current = state.conversations[requestId];
      if (!current) {
        settleLoadingIfLatest();
        return;
      }

      let merged: ChatMessage[];
      if (current.messages.length === 0) {
        merged = messages;
      } else if (messages.length === 0) {
        merged = current.messages;
      } else {
        merged = mergeHydratedMessages(messages, current.messages);
      }

      const nextPatch: Partial<SelectionEffectsState> = {
        conversations: {
          ...state.conversations,
          [requestId]: {
            ...current,
            messages: clampMessages(merged),
            historyLoaded: !fromCache,
            startPagePending: false,
          },
        },
      };

      if (isLatestActivation()) {
        clearSelectionLoadingIndicator();
        nextPatch.loadingConversationId = null;
      } else {
        trackStaleLoadingGuard();
      }
      setState(nextPatch);
    };

    void import('@auw/api-client').then(({ getCachedMessages, setCachedMessages }) => {
      getCachedMessages(requestId).then((cached) => {
        if (cached && cached.length > 0) {
          applyMessages(cached, true);
        }
      });

      fetchMessageHistory(requestId).then((backendMessages) => {
        applyMessages(backendMessages, false);
        if (backendMessages.length > 0) {
          void setCachedMessages(requestId, backendMessages);
        }
      }).catch(() => {
        settleLoadingIfLatest();
      });
    });
  }

  if (nextConversationId) {
    window.setTimeout(() => {
      if (getState().activeId !== nextConversationId) return;
      void loadConversationFiles(nextConversationId);
    }, 0);
    return;
  }

  setState({ sessionFiles: [] });
}
